import { Form, message, Modal } from 'antd'
import { useEffect, useRef, useState, useCallback } from 'react'
import { createClinic, readBPMProfile, updateClinic } from '../utils/utils'
import { handleError } from '../../../lib/utils'
import coreReducer from '../../../../core/utils/reducer'
import CoreEnums from '../../../../core/utils/enums'
import { useDispatch, useSelector } from 'react-redux'
import { geocodeByAddress } from 'react-google-places-autocomplete'
import ClinicTemplates from '../utils/templates'
import { INITIAL_MACRO_STATE } from '../utils/constants'
import { MedPraxSchemeSearch } from '../../billing-profile/utils'
import { clinicTabsEnums } from '../config/clinic-tabs-enums'

const useClinicsForm = ({
  recordData,
  setRecordData,
  newRecord,
  setNewRecord,
  refreshView,
  handleTabChange,
  activeTab,
  setResetPoint,
  setUnsavedChanges
}) => {
  const dispatch = useDispatch()
  const coreState = useSelector((state) => state.core)
  const [loading, setLoading] = useState(false)
  const [deactivatePrompt, setDeactivatePrompt] = useState(false)
  const [specialFormState, setSpecialFormState] = useState([])
  const [physicalAddress, setPhysicalAddress] = useState(recordData?.physicalAddress)
  const inputRef = useRef(null)
  const [image, setImage] = useState(recordData?.clinicLogo || coreState?.entity?.entityIcon || '')
  const [bpmProfile, setBpmProfile] = useState(null)
  const [bpmConfig, setBpmConfig] = useState(recordData?.bpmConfig || [])

  useEffect(() => {
    if (bpmProfile && recordData?.bpmConfig?.length === 0) {
      let tmpBpmConfig = []

      for (const step of bpmProfile.data.processSteps) {
        if (step.key !== 'creation') {
          tmpBpmConfig.push({
            label: step.name,
            key: step.key
          })
        }
      }

      setBpmConfig(tmpBpmConfig)
      practiceDetailsForm.setFieldValue(ClinicTemplates.dataModel.bpmConfig.key, tmpBpmConfig)
      practiceDetailsForm.setFieldValue(ClinicTemplates.dataModel.bpmKey.key, bpmProfile?.data?.key)
    }

    // eslint-disable-next-line
  }, [bpmProfile])

  // Billing profile schemes state
  const [schemesLoading, setSchemesLoading] = useState(false)
  const [schemeOps, setSchemeOps] = useState([])

  // Macros state
  const [macros, setMacros] = useState(recordData?.billingMacros || [])
  const [macro, setMacro] = useState(INITIAL_MACRO_STATE)
  const [macroIndex, setMacroIndex] = useState(null)
  const [macroFormOpen, setMacroFormOpen] = useState(false)
  const [isNewMacro, setIsNewMacro] = useState(false)

  const [basicInfoForm] = Form.useForm()
  const [locationContactForm] = Form.useForm()
  const [practiceDetailsForm] = Form.useForm()
  const [servicesOfferedForm] = Form.useForm()
  const [servicesConfigForm] = Form.useForm()
  const [billingProfileForm] = Form.useForm()
  const [billingMacrosForm] = Form.useForm()

  const readBase64 = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = (error) => reject(error)
    })
  }, [])

  const handleGetBpmProfiles = useCallback(async () => {
    try {
      let tmpBpmProfile = await readBPMProfile('patient_journey')

      setBpmProfile(tmpBpmProfile)
    } catch (error) {
      message.error(handleError(error, true))
    }
  }, [])

  const handleGetSchemes = useCallback(async (page = 1) => {
    let response = []
    let tmpOps = []

    setSchemesLoading(true)

    try {
      response = await MedPraxSchemeSearch('', page)
      tmpOps = response.schemes.pageResult

      if (page < response.schemes.totalPages) {
        await handleGetSchemes(page + 1)
      }

      setSchemeOps((prev) => [...prev, ...tmpOps])
    } catch (e) {
      message.error(handleError(e, true))
    }

    setSchemesLoading(false)
  }, [])

  useEffect(() => {
    const initializeForm = () => {
      const formState = Object.entries(recordData || {}).map(([key, value]) => ({
        key,
        value
      }))
      setSpecialFormState(formState)
      handleGetBpmProfiles()
      handleGetSchemes()
    }
    initializeForm()
    // eslint-disable-next-line
  }, [])

  const closeTab = useCallback(() => {
    refreshView()
    const targetKey = newRecord ? `${CoreEnums.tabKeys.CLINICS}_new}` : `${CoreEnums.tabKeys.CLINICS}_${recordData._id}`

    dispatch(
      coreReducer.actions.closeTab({
        targetKey,
        removeBreadcrumb: true
      })
    )
    // eslint-disable-next-line
  }, [])

  const handleSubmit = async (values) => {
    let createFacility = false
    if (newRecord) {
      switch (activeTab) {
        case clinicTabsEnums.BASIC_INFORMATION:
          handleTabChange(clinicTabsEnums.LOCATION_CONTACT)

          return
        case clinicTabsEnums.LOCATION_CONTACT:
          handleTabChange(clinicTabsEnums.PRACTICE_DETAILS)

          return
        case clinicTabsEnums.PRACTICE_DETAILS:
          handleTabChange(clinicTabsEnums.SERVICES_OFFERED)

          return
        case clinicTabsEnums.SERVICES_OFFERED:
          handleTabChange(clinicTabsEnums.SERVICES_CONFIG)

          return
        case clinicTabsEnums.SERVICES_CONFIG:
          handleTabChange(clinicTabsEnums.BILLING_PROFILE)

          return
        case clinicTabsEnums.BILLING_PROFILE:
          handleTabChange(clinicTabsEnums.BILLING_MACROS)

          return
        case clinicTabsEnums.BILLING_MACROS:
          createFacility = true
      }
    }

    try {
      setLoading(true)
      const record = values
      const clinicData = {
        ...record,
        clinicLogo: image,
        billingMacros: macros
      }

      if (createFacility) {
        const tmpData = await createClinic(clinicData)
        setRecordData(tmpData)
        setNewRecord(false)
        setResetPoint(tmpData)
        Modal.success({
          title: 'Facility Created',
          content: 'Facility created successfully! You can now freely edit the facility details.'
        })
      } else {
        const tmpData = await updateClinic({ _id: recordData._id, ...clinicData })

        setResetPoint({ ...recordData, ...clinicData })
        setRecordData(tmpData)
        message.success('Clinic updated successfully!')
      }

      // closeTab()
      setUnsavedChanges(false)
      dispatch(coreReducer.actions.setUnsavedChanges(false))
      // TODO: Deprecated ?
      if (coreState.tourOpen) {
        dispatch(coreReducer.actions.nextTourStep())
      }
    } catch (error) {
      message.error(handleError(error, true))
    } finally {
      setLoading(false)
      setDeactivatePrompt(false) // Reset prompt after submission
    }
  }

  const handleFormSubmit = async (values) => {
    await handleSubmit(values)
  }

  const handleIsActive = useCallback(
    (value) => {
      // Removed deactivation prompt logic - now handled in handleFormSubmit
    },
    // eslint-disable-next-line
    [recordData]
  )

  const handleGetAddress = async (address) => {
    try {
      const [result] = await geocodeByAddress(address)
      const formattedAddress = result.formatted_address
      locationContactForm.setFieldValue(ClinicTemplates.dataModel.physicalAddress.key, formattedAddress)
      setPhysicalAddress(formattedAddress)
    } catch (error) {
      message.error(handleError(error, true))
    }
  }

  const handleUploadClick = useCallback(() => {
    inputRef.current?.click()
    // eslint-disable-next-line
  }, [])

  const handleFileChange = async (e) => {
    try {
      const files = e.target?.files
      if (!files?.length) return

      const base64File = await readBase64(files[0])
      setImage(base64File)
    } catch (error) {
      message.error(handleError(error, true))
    }
  }

  const handleMacroDelete = useCallback((index) => {
    setMacros((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const handleMacroClick = useCallback((record, rowIndex) => {
    setMacro(record)
    setMacroFormOpen(true)
    setIsNewMacro(false)
    setMacroIndex(rowIndex)
  }, [])

  const handleNewMacro = () => {
    setMacro(INITIAL_MACRO_STATE)
    setMacroFormOpen(true)
    setIsNewMacro(true)
    setMacroIndex(null)
  }

  return {
    coreState,
    basicInfoForm,
    locationContactForm,
    practiceDetailsForm,
    servicesOfferedForm,
    servicesConfigForm,
    billingProfileForm,
    billingMacrosForm,
    macros,
    macro,
    macroIndex,
    macroFormOpen,
    isNewMacro,
    inputRef,
    physicalAddress,
    loading,
    deactivatePrompt,
    bpmProfile,
    bpmConfig,
    image,
    specialFormState,
    schemesLoading,
    schemeOps,
    handleSubmit,
    handleFormSubmit,
    handleFileChange,
    handleIsActive,
    handleGetAddress,
    handleUploadClick,
    setMacros,
    setMacro,
    setMacroIndex,
    setMacroFormOpen,
    setIsNewMacro,
    setPhysicalAddress,
    setLoading,
    setDeactivatePrompt,
    setBpmProfile,
    setBpmConfig,
    setImage,
    closeTab,
    handleMacroDelete,
    handleMacroClick,
    handleNewMacro,
    setSpecialFormState
  }
}

export default useClinicsForm
